import { z } from "zod";

// The signed-in person: who they are, their preferences, their ways to sign
// in. The plan, credits, limits and features belong to the organization now
// (ADR-003) and live in modules/billing.

export const userSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.email(),
  /** Short display name used in greetings ("Welcome back, RW."). */
  displayName: z.string(),
  /** Whether this address has been proven (auth owns the proof itself; this is
   * its own copy, kept in step by `setEmailVerified`, for a peer like
   * organization's invitations (S3.05) to read without depending on auth). */
  emailVerified: z.boolean(),
  /** A small uploaded picture, as a `data:<mime>;base64,<data>` URL; null falls back to
   * initials everywhere the avatar shows (S6.03). */
  avatarUrl: z.string().nullable(),
});
export type User = z.infer<typeof userSchema>;

export const emptyInputSchema = z.object({});

/** Where plan prices and invoices are shown back (S6.06); euro cents are the one stored unit
 * (`billing.schemas.ts`'s `PLAN_OFFERS`), converted to this at render time. */
export const currencySchema = z.enum(["EUR", "USD"]);
export type Currency = z.infer<typeof currencySchema>;

// `communications` and `renewalReminders` moved to notification's own preference store
// (S6.09, Decided in spec): account kept a second, cosmetic copy of a toggle that never
// suppressed an email; notification already owns every email preference that does.
// S6.04/S6.05: the languages the account preference accepts. "en" and "es" have a
// translated catalogue (`@amtana/i18n`'s shipped set, Decided in spec D104); the other four
// remain a real, accepted choice with no catalogue yet, so a screen falls back to English
// per key rather than the value being refused.
export const languageSchema = z.enum(["en", "es", "pt", "it", "fr", "de"]);

export const preferencesSchema = z.object({
  language: languageSchema,
  currency: currencySchema,
  timezone: z.string().min(1, "Choose a time zone."),
  earlyRelease: z.boolean(),
});
export type Preferences = z.infer<typeof preferencesSchema>;

/** Ways to sign in besides email and password: whether each provider is linked (auth ADR-007). */
export const signInLinksSchema = z.object({ google: z.boolean(), linkedin: z.boolean() });
export type SignInLinks = z.infer<typeof signInLinksSchema>;
export type SignInLinkProvider = keyof SignInLinks;

export const profileSchema = z.object({
  user: userSchema,
  preferences: preferencesSchema,
  signIn: signInLinksSchema,
});
export type Profile = z.infer<typeof profileSchema>;

// A changed email crosses into auth (it owns the sign-in credential, S6.02): `updateProfile`
// only ever touches the name now. `AccountApi.setEmail` is auth's own way to update account's
// copy once a mailed confirmation link is spent.
export const updateProfileInputSchema = z.object({
  firstName: z.string().trim().min(1, "Full name is required").max(60, "Use 60 characters or fewer."),
  lastName: z.string().trim().max(60, "Use 60 characters or fewer."),
});
export type UpdateProfileInput = z.infer<typeof updateProfileInputSchema>;

// Uploading an avatar (S6.03): the browser reads the chosen file as a data URL
// (`FileReader.readAsDataURL`) and posts it whole; `@amtana/media`'s
// `decodeImageUpload` checks its size and type before account ever stores it.
export const uploadAvatarInputSchema = z.object({ dataUrl: z.string().min(1, "Choose an image.") });
export type UploadAvatarInput = z.infer<typeof uploadAvatarInputSchema>;

/** The back office's own people search (S9.04, ADR-005 point 4): every account whose
 * email contains the query, case-insensitive. */
export const searchUsersInputSchema = z.object({ query: z.string().trim().min(1) });
export type SearchUsersInput = z.infer<typeof searchUsersInputSchema>;

// Erasure (ADR-026, epic 30, S30.01): who erased the person, and why. Staff's own `reason` is
// internal, required at the door that takes it (S30.03, out of this story's scope) and never
// echoed back to the person; `requestedVia`, when staff was given a channel, is (S30.13, D810).
export const ERASED_BY = ["self", "staff"] as const;
export const erasedBySchema = z.enum(ERASED_BY);
export type ErasedBy = (typeof ERASED_BY)[number];

export const REQUESTED_VIA = ["email", "phone", "chat"] as const;
export const requestedViaSchema = z.enum(REQUESTED_VIA);
export type RequestedVia = (typeof REQUESTED_VIA)[number];

// review group 2, decision g: a staff-driven erasure must name who did it and why (an empty
// reason is as good as none); a self-service one needs neither, the person acting being the one
// erased. The reason is capped at 500 characters: free text for an audit trail, not a document,
// and kept off the fact itself (never appended to account.erasure_initiated, audit-only, read
// back from the account row's own `erasure` field) so the event log carries no free text an
// erasure's own facts would otherwise ask redaction to find and clear later.
export const initiateErasureInputSchema = z
  .object({
    erasedBy: erasedBySchema,
    staffId: z.string().optional(),
    reason: z.string().max(500, "Keep the reason to 500 characters or fewer.").optional(),
    requestedVia: requestedViaSchema.optional(),
    /** The person's sole/personal organization ids (D879-D881, billing ordering fix), staff's own
     * door: computed before anything is read or written (`PersonErasureService.erase`), the
     * same point self-service computes them at its own gate (`ErasureGateService.deleteAccount`)
     * and carries them here through `record.deletion.organizationIds` instead, since a
     * staff-driven erasure never calls `requestDeletion` to persist them there first. Optional
     * because self-service never passes this: `initiateErasure` falls back to the row's own
     * stored value for that path. */
    organizationIds: z.array(z.string()).optional(),
  })
  .superRefine((input, ctx) => {
    if (input.erasedBy !== "staff") return;
    if (!input.staffId)
      ctx.addIssue({
        code: "custom",
        path: ["staffId"],
        message: "A staff-driven erasure needs who did it.",
      });
    if (!input.reason)
      ctx.addIssue({ code: "custom", path: ["reason"], message: "A staff-driven erasure needs a reason." });
  });
export type InitiateErasureInput = z.infer<typeof initiateErasureInputSchema>;

// `pending` (review group 2, decision e) answers whether a deletion is currently requested at
// all (`record.deletion !== null`): the day-before reminder wake's own re-check needs this, not
// only `initiated`, to tell a deletion that was cancelled (`deletion` cleared outright) apart
// from one still waiting on its own grace period.
export const erasureProgressSchema = z.object({ initiated: z.boolean(), pending: z.boolean() });
export type ErasureProgress = z.infer<typeof erasureProgressSchema>;

// The erasure managers' own facts (epic 44, S44.07, D1392): the managers run in worker-bridge,
// and only account may append account's facts, so each arrives here with the key it is appended
// under, the dead intent's or the settling intent's, as the Go worker appended them.

const failedStep = {
  /** The key the fact is appended under; a second call with it states nothing new. */
  idempotencyKey: z.string().min(1),
  attempts: z.number().int().nonnegative(),
  /** The failure code that ended the last attempt, or null when none was kept. */
  lastFailureCode: z.string().nullable(),
};

/** A module's erase step spent every retry: `account.erasure_step_failed`, naming the module. */
export const recordErasureStepFailedInputSchema = z.object({
  ...failedStep,
  personId: z.string().min(1),
  module: z.string().min(1),
});
export type RecordErasureStepFailedInput = z.infer<typeof recordErasureStepFailedInputSchema>;

/** The "erasure done" email settled: `account.erasure_notify_settled`. With `lapsed`, its 24-hour
 * window was spent with nothing sent, and `erasure_step_failed` naming "notify" is appended first,
 * in the same append, so the fan-out starts either way. */
export const settleErasureNotifyInputSchema = z.object({
  idempotencyKey: z.string().min(1),
  personId: z.string().min(1),
  email: z.string(),
  organizationIds: z.array(z.string()),
  lapsed: z.object(failedStep).optional(),
});
export type SettleErasureNotifyInput = z.infer<typeof settleErasureNotifyInputSchema>;

export const openAccountInputSchema = z.object({
  userId: z.string().regex(/^[\w-]{1,64}$/, "That user id isn't valid."),
  firstName: z.string().trim().min(1, "Please enter your first name.").max(60, "Use 60 characters or fewer."),
  lastName: z.string().trim().max(60, "Use 60 characters or fewer."),
  email: z.email("Please enter a valid email address."),
  communications: z.boolean(),
  /** The language picked before signing up (the sign-in switcher, S6.04); the service
   * falls back to English when the sign-up form carried none. Optional, not defaulted
   * here, so an existing caller that builds this input by hand keeps compiling. */
  language: languageSchema.optional(),
});
export type OpenAccountInput = z.infer<typeof openAccountInputSchema>;
